import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { actions } from 'store/printTemplate';
import { getSurveys } from 'store/surveys/actions';
import { produce } from 'immer';
import FormController from '../../common/formController';
import Joi from 'joi';
import validators from './validators';
import { constants } from 'views/Campaign';
import LicenseCheck from 'LicenseCheck';
import PermissionCheck from 'PermissionsCheck.js';
import { exportGrapeJsContent, encodeTemplateContent, sanitizeData } from 'store/helpers/helperFunctions';
import { isReadOnlyMode } from 'store/helpers/helperFunctions';
import { useLanguageSettings } from '../Settings/useLanguageSettings'
import { getEnvVar } from 'helpers';

const {
  getPrintTemplate,
  setCurrentPrintTemplate,
  updatePrintTemplate,
  createPrintTemplate,
  printAutoSaveTimerStart,
  getPrintFile,
  downloadedPrintFile,
  setTestDownload
} = actions;

const {
  ON_DEMAND,
  ON_EVENT,
  INTERNAL_SURVEY_RESPONSE
} = constants.campaignOccurrenceTypes;

let formController = new FormController();

export default function useTemplate(nextPath, backPath) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { templateId } = useParams();
  const isAutoSaveDisabled = getEnvVar('ADM_DISABLE_AUTO_SAVE') === 'true';
  const isAutoSaveTimerRunning = useSelector(store => store.printTemplate.isAutoSaveTimerRunning);
  const template = useSelector(store => store.printTemplate.current);

  const isTemplateSaved = useSelector(
    store => store.printTemplate.isTemplateSaved
  );
  const isTestDownloadError = useSelector(
    store => store.printTemplate.isTestDownloadError
  );
  const shouldAutoSave = useSelector(
    store => store.printTemplate.shouldAutoSave
  );
  const isAutoSaveSuccess = useSelector(
    store => store.printTemplate.isAutoSaveSuccess
  );
  const isTemplateSaving = useSelector(
    store => store.printTemplate.isTemplateSaving
  );
  const isLoading = useSelector(store => store.printTemplate.getting);
  const isTestTemplateLoading = useSelector(store => store.printTemplate.printFile.getting);
  const supportedEventTypeOptions = useSelector(
    store => store.events.supportedEventTypes.options
  );
  const supportedPrintTypeOptions = useSelector(
    store => store.events.supportedPrintTypes
  );
  const productIntegrationOptions = useSelector(
    store => store.events.supportedEventTypes.productIntegrationOptions
  );
  const supportedScheduleTypeOptions = useSelector(
    store => store.events.supportedScheduledTypes
  );
  const supportedOnDemandTypeOptions = useSelector(
    store => store.events.supportedOnDemandTypes
  );
  const supportedInternalSurveyResponseTypeOptions = useSelector(
    store => store.events.supportedInternalSurveyResponseTypes
  );
  const allSurveys = useSelector(store => store.survey.list);

  const currentTenantId = useSelector(
    store => store.user.selectedTenant.tenantId
  );
  const currentBusinessContextId = useSelector(
    store => store.user.selectedBusinessContext.businessContextId
  );

  const settingsMetaDataStore = useSelector(
    store => store.settings.settingsMetaData.data
  );

  const gettingFileSucceeded = useSelector(store => store.printTemplate.printFile.gettingFileSucceeded);
  const gettingFile = useSelector(store => store.printTemplate.printFile.gettingFile);
  const printFileBlob = useSelector(store => store.printTemplate.printFile.printFileBlob);

  const licenses = LicenseCheck();
  const { isLicenseLoading, canAccessSurvey } = licenses;

  const permissions = PermissionCheck();
  const { hasPermission } = permissions;

  const [validationErrors, setValidationErrors] = useState({});
  const [errorValidationSegment, setErrorValidationSegment] = useState(null);
  const { templateLanguageContent } = useLanguageSettings();
  const [thisTemplate, setThisTemplate] = useState({
    type: 'PDF',
    name: '',
    languageCode: settingsMetaDataStore?.languageCode,
    headerHeight: 0,
    footerHeight: 0
  });

  const validate = (saveAttempted = false) => {
    let segment = errorValidationSegment;
    const joiValidators = Joi.object(validators[segment]);
    const validation = joiValidators.validate(thisTemplate, {
      abortEarly: false,
      allowUnknown: true // allow unknown keys that will be ignored
    });
    let errors = formController.getFormErrors(validation, { saveAttempted });
    setValidationErrors(errors);
    return errors;
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!isLoading) {
      dispatch(getPrintTemplate({ templateId }));
    }
    if (Object.keys(template).length > 0) {
      formController = new FormController();
      setThisTemplate({ ...thisTemplate, ...template });
    }
  }, [templateLanguageContent]);

  useEffect(() => {
    if (Object.keys(template).length > 0) {
      formController = new FormController();
      let schemaIdentifier = template.productCategory
        ? `${template.productCategory}.${template.productName}.${template.eventTypeCategory}`
        : '';

      setThisTemplate({ ...thisTemplate, ...template, schemaIdentifier });
    }

    if (!templateId) {
      setThisTemplate({
        ...thisTemplate,
        ...template,
        hasMovedToNextStep: template.hasMovedToNextStep || false
      });
    }
  }, [template]);

  useEffect(() => {
    if (!gettingFile) {
      if (gettingFileSucceeded && printFileBlob.type === 'application/pdf') {
        const file = new Blob([printFileBlob], { type: 'application/pdf' });  
        const fileURL = URL.createObjectURL(file);  
        window.open(fileURL);  
      }
      dispatch(downloadedPrintFile());
    }
  }, [gettingFileSucceeded]);

  const goNext = event => {
    const errors = validate(true);
    if (errors.meta.clean) {
      dispatch(
        setCurrentPrintTemplate({
          ...thisTemplate,
          hasMovedToNextStep: true
        })
      );
      formController = new FormController();
      history.push(
        `${nextPath || event.currentTarget.attributes.link.value}${
          templateId ? `/${templateId}` : ''
        }`
      );
    }
    startAutoSaveTimer();
  };

  useEffect(() => {
    !isLicenseLoading &&
      canAccessSurvey &&
      hasPermission('Rguest_readSurvey') &&
      dispatch(getSurveys());
  }, [isLicenseLoading]);

  const goBack = event => {
    dispatch(setCurrentPrintTemplate({ ...thisTemplate }));
    history.push(
      `${backPath || event.currentTarget.attributes.link.value}${
        templateId ? `/${templateId}` : ''
      }`
    );
  };

  const navBackOnError = path => {
    if (
      thisTemplate.hasOwnProperty('hasMovedToNextStep') &&
      !thisTemplate.hasMovedToNextStep &&
      !thisTemplate.name
    ) {
      dispatch(
        setCurrentPrintTemplate({
          ...thisTemplate,
          hasMovedToNextStep: false
        })
      );
      history.push(path);
    }
  };

  const onTabChange = () => {
    dispatch(setCurrentPrintTemplate(thisTemplate));
  };

  // setByPath({}, 'key.key', 123);
  const setByPath = (obj, path, value) => {
    if (typeof path === 'string') {
      path = path.split('.');
    }
    if (obj && path.length === 1) {
      obj[path] = value;
      return;
    }
    return setByPath(obj[path[0]], path.slice(1), value);
  };

  const handleChange = events => {
    events = Array.isArray(events) ? events : [events];

    let updatedTemplate = produce(thisTemplate, templateDraft => {
      if (events.length > 0) {
        events.forEach(event => {
          setByPath(templateDraft, event.target.name, event.target.value);
        });
      }
    });

    setThisTemplate(updatedTemplate);
    validate();
  };

  const handleAutomationChange = event => {
    const { name, value } = event.target;
    let typeOption = {};

    let templateUpdate = produce(thisTemplate, draft => {
      typeOption = supportedPrintTypeOptions?.find(
        option => option.id === value
      );

      draft.productCategory = typeOption.productCategory;
      draft.productName = typeOption.productName;
      draft.eventTypeCategory = typeOption.eventTypeCategory;
      draft.schemaId = typeOption.schemaId;
      setByPath(draft, name, value);
    });

    setThisTemplate(templateUpdate);
    validate(false);
  };

  const handlePermissions = checkPermission => {
    return hasPermission(checkPermission);
  };

  const saveTemplate = () => {
    const errors = validate(true);
    if (errors.meta.clean) {
      dispatch(setCurrentPrintTemplate({ ...thisTemplate }));
      if (thisTemplate.id) {
        dispatch(updatePrintTemplate());
      } else {
        dispatch(createPrintTemplate());
      }
      formController = new FormController();
    }
  };

  const onClickTestTemplate = (editor) => {
    // Ensure valid rows and columns for test template - default to 1 if invalid
    const rowsNum = Number(thisTemplate.rows);
    const columnsNum = Number(thisTemplate.columns);
    const rows = (rowsNum >= 1 && rowsNum <= 4) ? rowsNum : 1;
    const columns = (columnsNum >= 1 && columnsNum <= 3) ? columnsNum : 1;
    const sanitizedTemplate = {
      ...thisTemplate,
      rows: rows,
      columns: columns,
      jsonContent: exportGrapeJsContent(editor.html, editor.css),
      content: exportGrapeJsContent(editor.html, editor.css)?.html
    };
    let encodedTemplate = encodeTemplateContent(sanitizedTemplate);
    dispatch(getPrintFile(encodedTemplate));
  }

  const onClickTestTemplateClose = () => {
    dispatch(setTestDownload(false));
  }
  const closeTemplate = () => {
    formController = new FormController();
    history.push('/print-templates');
  };

  const onBlur = e => {
    // Sanitize event data before processing
    const sanitizedValue = sanitizeData(e.target.value);
    const sanitizedName = sanitizeData(e.target.name);
    
    const sanitizedEvent = {
      ...e,
      target: {
        ...e.target,
        value: sanitizedValue,
        name: sanitizedName
      }
    };

    // Update local state with sanitized value
    if (sanitizedName && sanitizedValue !== e.target.value) {
      const updatedTemplate = produce(thisTemplate, draft => {
        setByPath(draft, sanitizedName, sanitizedValue);
      });
      setThisTemplate(updatedTemplate);
    }

    formController.onBlur(sanitizedEvent);
    validate();
  };

  const onFocus = e => {
    // Sanitize event data before processing
    const sanitizedValue = sanitizeData(e.target.value);
    const sanitizedName = sanitizeData(e.target.name);
    
    const sanitizedEvent = {
      ...e,
      target: {
        ...e.target,
        value: sanitizedValue,
        name: sanitizedName
      }
    };

    // Update local state with sanitized value
    if (sanitizedName && sanitizedValue !== e.target.value) {
      const updatedTemplate = produce(thisTemplate, draft => {
        setByPath(draft, sanitizedName, sanitizedValue);
      });
      setThisTemplate(updatedTemplate);
    }

    formController.onFocus(sanitizedEvent);
    validate();
  };

  const isTemplateReadOnly = () => {
    return (
      thisTemplate.id &&
      (currentTenantId !== thisTemplate.tenantId ||
        currentBusinessContextId !== thisTemplate.businessContextId)
    ) || isReadOnlyMode();
  };

  const showWarningMessage = () => {
    return (
      isDefaultTemplate() &&
      thisTemplate.tenantId === currentTenantId &&
      thisTemplate.businessContextId === currentBusinessContextId
    );
  };

  const isDefaultTemplate = () => {
    return thisTemplate.id && template.defaultTemplate;
  };

  const showDefaultCheckbox = () => {
    return (
      (currentTenantId === '0' && currentBusinessContextId === 'Default') ||
      thisTemplate.defaultTemplate
    );
  };

  const toggleDefaultTemplate = () => {
    setThisTemplate({
      ...thisTemplate,
      defaultTemplate: !thisTemplate.defaultTemplate
    });
  };

  const isTenantDefaultTemplate = () => {
    return (
      (!thisTemplate.defaultTemplate ||
        thisTemplate.tenantId === currentTenantId) &&
      thisTemplate.businessContextId !== currentBusinessContextId
    );
  };

  const shouldShowError = name => {
    return formController.shouldShowError(validationErrors, name);
  };

  const getErrorMessage = name => {
    return formController.getErrorMessage(validationErrors, name);
  };

  const showFormSaveError = () => {
    return formController.showFormSaveError(validationErrors);
  };

  const updateCurrentErrorValidationSegment = name => {
    setErrorValidationSegment(name);
  };

  const startAutoSaveTimer = () => {
    if (template.tenantId === currentTenantId && template.businessContextId === currentBusinessContextId
      && !isLoading && !isAutoSaveTimerRunning && !isAutoSaveDisabled
    ) {
      dispatch(printAutoSaveTimerStart());
    }
  };

  const autoSaveTemplate = () => {
    dispatch(setCurrentPrintTemplate({ ...thisTemplate }));
    if (thisTemplate.id) {
      dispatch(updatePrintTemplate());
    } else {
      dispatch(createPrintTemplate());
    }
    formController = new FormController();
  };

  const modifyTags = updatedTags => {
    setThisTemplate({
      ...thisTemplate,
      tags: [...updatedTags]
    });
  };

  const modifyRows = updatedRows => {
    setThisTemplate({
      ...thisTemplate,
      rows: updatedRows
    });
  };

  const modifyColumns = updatedColumns => {
    setThisTemplate({
      ...thisTemplate,
      columns: updatedColumns
    });
  };

  return {
    thisTemplate,
    validationErrors,
    handleChange,
    handleAutomationChange,
    handlePermissions,
    goNext,
    goBack,
    navBackOnError,
    onTabChange,
    saveTemplate,
    onClickTestTemplate,
    onClickTestTemplateClose,
    closeTemplate,
    onFocus,
    onBlur,
    autoSaveTemplate,
    showFormSaveError,
    getErrorMessage,
    shouldShowError,
    startAutoSaveTimer,
    updateCurrentErrorValidationSegment,
    modifyTags,
    modifyRows,
    modifyColumns,
    isTemplateReadOnly,
    showWarningMessage,
    isDefaultTemplate,
    showDefaultCheckbox,
    toggleDefaultTemplate,
    isTenantDefaultTemplate,
    supportedEventTypeOptions,
    supportedScheduleTypeOptions,
    supportedOnDemandTypeOptions,
    supportedInternalSurveyResponseTypeOptions,
    supportedPrintTypeOptions,
    productIntegrationOptions,
    allSurveys,
    isLoading,
    isTestTemplateLoading,
    shouldAutoSave,
    isTemplateSaved,
    isTemplateSaving,
    isTestDownloadError,
    isAutoSaveSuccess
  };
}
